









import { Component, Prop } from 'vue-property-decorator';
import { MapBlockViewModel, MapLocationBlockViewModel } from '@/types/contentServerContract';
import AspectRatioBlockContainer from '@/project/app/blocks/AspectRatioBlockContainer.vue';
import { Loader } from '@googlemaps/js-api-loader';
import serverContextService from '@/core/serverContext.service';

interface Position {
    lat: number;
    lng: number;
}

// ref: https://developers.google.com/maps/documentation/javascript/reference/marker#MarkerOptions
interface Marker {
    clickable: boolean;
    draggable: boolean;
    icon: {
        url: string;
        size: {
            width: number;
            height: number;
        },
        scaledSize: {
            width: number;
            height: number;
        }
    };
    position: Position;
    data: MapLocationBlockViewModel;
}

// ref: https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
interface MapOptions {

    tilt?: number;
    zoom?: number; // The initial Map zoom level. Valid values: Integers between 0 - 18.
    minZoom?: number;
    maxZoom?: number;
    zoomControl?: boolean; // The enabled/disabled state of the Zoom control.
    zoomControlOptions?: {
        position: 'BOTTOM_CENTER' | 'BOTTOM_LEFT' | 'BOTTOM_RIGHT' | 'LEFT_BOTTOM' | 'LEFT_CENTER' | 'LEFT_TOP' | 'RIGHT_BOTTOM' | 'RIGHT_CENTER' | 'RIGHT_TOP' | 'TOP_CENTER' | 'TOP_LEFT' | 'TOP_RIGHT';
    },
    mapTypeControl: boolean;
    scaleControl: boolean;
    streetViewControl: boolean;
    rotateControl: boolean;
    fullscreenControl: boolean;
    disableDefaultUI: boolean;
    draggable: boolean;
    clickableIcons: boolean;
    scrollwheel?: boolean;
    disableDoubleClickZoom?: boolean;
    keyboardShortcuts?: boolean;
    styles: any[];
}

@Component()
export default class GoogleMapsBlock extends AspectRatioBlockContainer {
    @Prop({
        required: true,
        type: Object
    }) content!: MapBlockViewModel;

    @Prop({
        required: true,
        type: Boolean
    }) isFirstBlock!: boolean;

    map!: google.maps.Map;
    google!: google.maps;

    center: Position = { lat: 55.838395, lng: 12.402816 };
    infoWindow: {
        activeMarker: Marker | null,
        position: Position | null,
        open: boolean
    } = {
        activeMarker: null,
        position: null,
        open: false
    }

    defaultZoom: number = 6;
    loader: null | Loader = null;

    markerClicked(marker: Marker) {
        this.infoWindow.activeMarker = marker;
        this.infoWindow.position = marker.position;
        this.infoWindow.open = true;
    }

    hideInfoWindow() {
        this.infoWindow.open = false;
        this.infoWindow.position = null;
        this.infoWindow.activeMarker = null;
    }

    mapMarkerTemplate(color: string): string {
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 24" width="18" height="24" fill="none"><style type="text/css">.st0{fill:${color};}</style><g id="Design"><g id="Kontaktside" transform="translate(-1141.000000, -545.000000)"><g id="Group-3" transform="translate(845.000000, 328.000000)"><g id="Group-2" transform="translate(296.000000, 217.000000)"><path id="Combined-Shape" class="st0" d="M9.1,0c1.4,0,2.7,0.2,3.8,0.7s2,1.1,2.8,2s1.3,1.8,1.7,2.9S18,7.9,18,9.1c0,1.2-0.2,2.5-0.7,3.8s-1.1,2.6-1.9,3.8s-1.6,2.4-2.4,3.6s-1.7,2.1-2.5,3c0,0-0.1,0.1-0.4,0.3S9.6,24,9.3,24l0,0H9.3c-0.3,0-0.6-0.1-0.8-0.3s-0.3-0.3-0.3-0.3c-0.8-0.9-1.7-2-2.6-3.1S3.6,18,2.8,16.8s-1.5-2.5-2-3.8S0,10.4,0,9.1c0-1.2,0.2-2.4,0.7-3.5s1.1-2.1,2-2.9s1.8-1.5,2.9-2S7.9,0,9.1,0z M9,4.5C7.8,4.5,6.7,5,5.8,5.8S4.5,7.8,4.5,9c0,1.2,0.4,2.3,1.3,3.2s1.9,1.3,3.2,1.3c1.2,0,2.3-0.4,3.2-1.3s1.3-1.9,1.3-3.2c0-1.2-0.4-2.3-1.3-3.2S10.3,4.5,9,4.5z"/></g></g></g></g></svg>`;
    };

    get markers(): Marker[] {
        const mapMarkerBlack = this.mapMarkerTemplate('#000000');
        const mapMarkerTurqoise = this.mapMarkerTemplate('#2D92AE');
        const mapMarkerBlue = this.mapMarkerTemplate('#3062A9');
        const mapMarkerGreen = this.mapMarkerTemplate('#006A4D');

        const markerOffice = this.isOprema ? mapMarkerTurqoise : mapMarkerBlue;
        const markerOther = this.isOprema ? mapMarkerBlack : mapMarkerGreen;
        return (this.content.mapLocations)
            .map(({ content }) => content as MapLocationBlockViewModel)
            .map(x => ({
                clickable: true,
                draggable: false,
                icon: {
                    url: x.locationType === 'Office'
                        ? markerOffice
                        : markerOther
                },
                position: {
                    lat: parseFloat(x.gpsLatitude),
                    lng: parseFloat(x.gpsLongitude)
                },
                data: x
            } as Marker));
    }

    styles =
         [
             {
                 featureType: 'administrative',
                 elementType: 'all',
                 stylers: [
                     {
                         visibility: 'off'
                     }
                 ]
             },
             {
                 featureType: 'landscape',
                 elementType: 'all',
                 stylers: [
                     {
                         visibility: 'on'
                     },
                     {
                         color: this.isOprema ? '#c0dfe7' : '#c5d2e0'
                     }
                 ]
             },
             {
                 featureType: 'landscape',
                 elementType: 'geometry',
                 stylers: [
                     {
                         visibility: 'on'
                     }
                 ]
             },
             {
                 featureType: 'landscape',
                 elementType: 'labels',
                 stylers: [
                     {
                         visibility: 'off'
                     }
                 ]
             },
             {
                 featureType: 'poi',
                 elementType: 'all',
                 stylers: [
                     {
                         visibility: 'off'
                     }
                 ]
             },
             {
                 featureType: 'road',
                 elementType: 'all',
                 stylers: [
                     {
                         visibility: 'off'
                     }
                 ]
             },
             {
                 featureType: 'transit',
                 elementType: 'all',
                 stylers: [
                     {
                         visibility: 'off'
                     }
                 ]
             },
             {
                 featureType: 'water',
                 elementType: 'all',
                 stylers: [
                     {
                         color: '#ffffff'
                     }
                 ]
             }
         ];

    get options(): MapOptions {
        return {
            maxZoom: this.content.maxZoom ? this.content.maxZoom : 9,
            minZoom: this.content.minZoom ? this.content.minZoom : 4,
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            disableDefaultUI: true,
            draggable: true,
            clickableIcons: true,
            scrollwheel: false,
            disableDoubleClickZoom: true,
            keyboardShortcuts: false,
            styles: this.styles
        };
    }

    get isOprema() {
        return serverContextService?.isOprema;
    }

    async getMap() {
        return this.google;
        // return await this.$refs.map.$mapPromise;
    }

    async setBoundsByMarkers() {
        const { LatLngBounds } = await this.loader.importLibrary('core') as google.maps.CoreLibrary;
        const bounds = new LatLngBounds();

        for (const marker of this.markers) {
            bounds.extend(marker.position);
        }
        this.map.fitBounds(bounds);
        this.map.panToBounds(bounds);

        // fitBounds will zoom in as much as possible, so we zoom back out if that's the case
        setTimeout(() => {
            if (this.map.getZoom() > this.defaultZoom) {
                this.map.setZoom(this.defaultZoom);
            }
        }, 500);
    }

    async syncCenterLocationWithUser() {
        if (!navigator.geolocation) return;

        const map = await this.getMap();

        navigator.geolocation.getCurrentPosition(({ coords }) => {
            map.setCenter({
                lat: coords.latitude,
                lng: coords.longitude
            });
        });
    }

    async initMapLoader() {
        this.loader = new Loader({
            apiKey: 'AIzaSyB0DhEqDe1BWbbTGL_ycHBCjroikRYz9Eo',
            version: 'weekly',
            language: window.servercontext.languageCode,
            region: window.servercontext.countryCode,
            libraries: ['places']
        });
    }

    async initMap() {
        const { Map, InfoWindow } = await this.loader.importLibrary('maps') as google.maps.MapsLibrary;

        const map = new Map(this.$refs.map as HTMLElement, {
            mapId: 'a3a60e30ec73b77c',
            center: this.center,
            maxZoom: this.content.maxZoom ? this.content.maxZoom : 9,
            minZoom: this.content.minZoom ? this.content.minZoom : 4,
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            disableDefaultUI: true,
            clickableIcons: true,
            scrollwheel: false,
            disableDoubleClickZoom: true,
            keyboardShortcuts: false
        });
        this.map = map;
        const styledMapType = new window.google.maps.StyledMapType(this.styles, { name: 'Styled Map' });
        map.mapTypes.set('styled_map', styledMapType);
        map.setMapTypeId('styled_map');
        return InfoWindow;
    }

    async initMarkers(InfoWindowScript: google.maps.InfoWindow) {
        const { AdvancedMarkerElement } = await window.google.maps.importLibrary('marker') as google.maps.MarkerLibrary;

        // Create an info window to share between markers.
        const infoWindow = new InfoWindowScript();
        const parser = new DOMParser();
        for (const marker of this.markers) {
            const markerSvg = parser.parseFromString(marker.icon.url, 'image/svg+xml').documentElement;
            const markerElement = new AdvancedMarkerElement({
                map: this.map,
                content: markerSvg,
                position: marker.position,
                title: marker.data.groupName,
                gmpClickable: true
            } as google.maps.MarkerLibrary.AdvancedMarkerOptions) as google.maps.MarkerLibrary.AdvancedMarkerElement;

            markerElement.addListener('click', () => {
                infoWindow.close();
                const contentLink = marker.data.link ? `<a
                       href="${marker.data.link.url}"
                       target="${marker.data.link?.target || '_self'}"
                       class="flex flex-row items-center theme-text-linkcolor underline mt-15">
                            <span>${marker.data.link.name}</span>
                            <CIcon name="arrow-right" height="10" width="10" class="ml-5"/>
                        </a>` : '';
                const phoneLink = marker.data.phoneNumber ? `<p class="mb-2"><a href="tel:${marker.data.phoneNumber}">${marker.data.phoneNumber}</a></p>` : '';
                const mailLink = marker.data.email ? `<a class="underline" href="mailto:${marker.data.email}" target="_blank">${marker.data.email}</a>` : '';
                const content = `<div class="p-20 bg-blue-400 text-14 flex flex-col">
                    <p class="font-bold mb-2">${marker.data.groupName}</p>
                    <p class="mb-2">${marker.data.address}</p>
                    <p class="mb-2">${marker.data.postalCodeAndCity}</p>
                    <p class="mb-2">${marker.data.country}</p>
                    ${phoneLink}
                    ${mailLink}
                    ${contentLink}
                    </div>`;
                infoWindow.setContent(content);
                infoWindow.open({ map: this.map, anchor: markerElement });
            });
        }
    }

    async mounted() {
        await this.initMapLoader();
        const infoWindow = await this.initMap();
        await this.initMarkers(infoWindow);
        if (this.markers.length) {
            this.setBoundsByMarkers();
        } else {
            this.syncCenterLocationWithUser();
        }
    }

    $refs!: {
        map: any;
    }
}
